import { useOrgPeople } from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import { Badge, Empty, humanStatus, statusTone } from "src/components/ui";
import type { PartnerOrganizationPeopleQuery } from "src/graphql/generated";
import { personDisplayName } from "src/lib/names";

type OrgPerson = PartnerOrganizationPeopleQuery["partnerOrganizationPeople"][number];

/**
 * ORG_ROSTER_LIMIT bounds one firm's roster.
 *
 * Named rather than inline so the panel can say when it is showing a capped page:
 * the server caps at 500 regardless, and a roster quietly shorter than the count it
 * was opened from is the one thing this feature must never do — that disagreement is
 * the bug it exists to resolve.
 */
export const ORG_ROSTER_LIMIT = 200;

/**
 * The people engaged through one organization, under the headcount they explain.
 *
 * Fetched when the row is expanded, never with the listing. The Organizations screen
 * is the whole table, so loading a roster per row would be the query-per-row the
 * headcount was made a single aggregate to avoid — and the payload would be every
 * partner in the database instead of a column of numbers.
 *
 * Grouped exactly as the column is split, with the same two words, because the panel
 * has to be readable as the number above it broken open. Both groups are shown: a
 * list quietly omitting the people who have left would be the reported bug again, and
 * "who left" is the question that was actually asked.
 */
export function OrgPeoplePanel({
  organizationId,
  headcount,
  onOpen
}: {
  organizationId: string;
  /**
   * The unscoped headcount from the row, or null where nobody counted. Passed in
   * rather than re-queried: it is the number this panel is being asked to explain,
   * and comparing it against what came back is how the panel knows whether it is
   * showing everybody.
   */
  headcount: number | null;
  onOpen: (identityId: string) => void;
}) {
  const { data, isPending, error } = useOrgPeople(organizationId, ORG_ROSTER_LIMIT);
  const people = data?.partnerOrganizationPeople ?? [];

  if (isPending) {
    return <Empty>Loading…</Empty>;
  }
  if (error) {
    return <ReadFailure error={error} />;
  }
  if (people.length === 0) {
    return <Empty>Nobody you can see is engaged through this organization.</Empty>;
  }

  const current = people.filter((p) => p.current);
  const ended = people.filter((p) => !p.current);

  // Two reasons the list can be shorter than the count above it, and they need
  // different sentences. Both start from the same difference.
  const short = headcount != null ? headcount - people.length : 0;
  // A full page with the count still ahead of it: there is more behind the cap, so
  // say so rather than truncate silently. The `short > 0` is what stops a firm with
  // exactly ORG_ROSTER_LIMIT people from being reported as truncated when the list is
  // in fact complete. With no count to compare against, a full page is assumed to
  // have more behind it, which is what the People list already does.
  const capped = people.length >= ORG_ROSTER_LIMIT && (headcount == null || short > 0);
  // Otherwise the difference is scope: the count is deliberately a total across both
  // populations and a list of names cannot be, so the rest are somebody else's to
  // read. Named rather than left as a shortfall the reader has to notice for
  // themselves — an unexplained one is what gets reported as a bug.
  //
  // A list that is both capped and scoped reads as capped. The two are
  // indistinguishable from here without another round trip, and "there may be more"
  // is the safer of the two things to tell somebody checking a number.
  const hidden = capped ? 0 : short;
  // And the difference can go the other way, which makes the count the stale one.
  // The two come from separate queries and so separate snapshots: the listing is
  // fetched when the screen opens and does not refetch on focus, so somebody engaged
  // through this firm since then is in the roster and not in the number above it.
  // Said rather than left as a contradiction the reader has to resolve on their own,
  // which is this feature's own failure mode pointing the other way.
  const stale = headcount != null && people.length > headcount;

  return (
    <div className="flex flex-col gap-3">
      {capped ? (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Showing the first {ORG_ROSTER_LIMIT}
          {headcount != null ? ` of ${headcount}` : ""}. Use the People tab to see them all.
        </p>
      ) : null}
      {stale ? (
        <p className="text-xs text-slate-500">
          This list has {people.length} people and the count above says {headcount}. The count was
          loaded when the screen opened; this is the newer of the two.
        </p>
      ) : null}
      {hidden > 0 ? (
        <p className="text-xs text-slate-500">
          Showing {people.length} of {headcount}. The{" "}
          {hidden === 1 ? "other is" : `other ${hidden} are`} in a population you cannot read, so
          the count above is higher than this list.
        </p>
      ) : null}

      <PeopleGroup
        label="Current"
        caption="Holding a term through this firm that has not ended."
        people={current}
        onOpen={onOpen}
      />
      <PeopleGroup
        label="Ended"
        caption="Every term they held through this firm is over."
        people={ended}
        onOpen={onOpen}
      />
    </div>
  );
}

/**
 * One of the two groups. Rendered even when empty, with the count, so the pair reads
 * as the column's two numbers rather than as a list that happens to be short —
 * "Ended 0" is the answer to a question a reader checking a headcount is asking.
 */
function PeopleGroup({
  label,
  caption,
  people,
  onOpen
}: {
  label: string;
  caption: string;
  people: readonly OrgPerson[];
  onOpen: (identityId: string) => void;
}) {
  return (
    <div>
      {/* h3, not h4: this panel opens inside a card whose title is an h2, so an h4
          here skips a level and puts a hole in the outline a screen reader navigates
          by. The size is the class's, not the level's, so the pair still reads as a
          caption under a heading. */}
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label} ({people.length})
      </h3>
      {/* slate-600 rather than slate-400, which was 2.5:1 against this panel's
          slate-50 row and so under the 4.5:1 body-text floor. Still the quietest
          thing in the group — the heading above it carries the weight. */}
      <p className="mt-0.5 text-xs text-slate-600">{caption}</p>
      {people.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">None.</p>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-1">
          {people.map((person) => (
            <PersonRow key={person.identityId} person={person} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </div>
  );
}

function PersonRow({
  person,
  onOpen
}: {
  person: OrgPerson;
  onOpen: (identityId: string) => void;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      {/* The name is the link, because the whole point of the count being drillable
          is getting to the record. Every entry reaches a page this reader may open:
          the roster and the person's own query are scoped by the same rule, so the
          list cannot offer a link that is then refused. */}
      <button
        type="button"
        className="text-sm font-medium text-slate-900 underline hover:text-slate-600"
        onClick={() => onOpen(person.identityId)}
      >
        {personDisplayName(person)}
      </button>
      {person.legalFullNameLocal ? (
        <span className="text-xs text-slate-500">{person.legalFullNameLocal}</span>
      ) : null}
      <Badge tone={statusTone(person.status)}>{humanStatus(person.status)}</Badge>
      <span className="text-xs text-slate-500">{termSummary(person)}</span>
    </li>
  );
}

/**
 * When their term ran, in the fewest words that answer what a reader checking a
 * headcount is asking.
 *
 * A termination and a contracted end are kept apart rather than collapsed into one
 * "left on" date. They are different facts — somebody decided, or the agreement
 * simply ran out — and a term cut short keeps the end date it was contracted for, so
 * showing one date for both would report an agreement that was never made.
 */
function termSummary(person: OrgPerson): string {
  const parts: string[] = [];
  if (person.startDate) {
    parts.push(`from ${person.startDate}`);
  }
  if (person.terminatedOn) {
    parts.push(`ended ${person.terminatedOn}`);
  } else if (person.endDate) {
    parts.push(`until ${person.endDate}`);
  }
  // Only worth saying when there is more than one, and worth saying then: the roster
  // is one entry per person, so without this a rehire through the same firm would
  // look like a single term.
  if (person.terms > 1) {
    parts.push(`${person.terms} terms`);
  }
  return parts.join(" · ");
}
